import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, Transition as _Transition, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "admin-dashboard" }
const _hoisted_2 = { class: "logo-wrapper" }
const _hoisted_3 = { class: "logo-text" }
const _hoisted_4 = { class: "toggle-collapse" }
const _hoisted_5 = { class: "header-content" }
const _hoisted_6 = { class: "page-title" }
const _hoisted_7 = { class: "user-actions" }
const _hoisted_8 = { class: "user-profile" }
const _hoisted_9 = { class: "user-info" }
const _hoisted_10 = { class: "username" }
const _hoisted_11 = { class: "role" }
const _hoisted_12 = {
  key: 0,
  class: "dynamic-content"
}

import { ref, onMounted, computed, markRaw } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { 
  HomeFilled, 
  Setting, 
  User, 
  UserFilled, 
  Lock, 
  Menu, 
  Document, 
  ChatDotSquare, 
  Checked, 
  DocumentCheck, 
  ChatCheck, 
  Warning,
  Management,
  ChatLineRound,
  SwitchButton
} from '@element-plus/icons-vue';
import { getCurrentUserInfo } from '@/admin/api/user'; // 导入获取当前用户信息的API
import { logout as adminLogout } from '@/admin/api/user'; // 导入退出登录API
import WelcomeSection from '@/admin/views/dashboard/DashboardView.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminHomeView',
  setup(__props) {

const router = useRouter();

// 响应式数据
const userMenus = ref<any[]>([]);
const userInfo = ref<any>(null);
const activeMenu = ref('');
const currentComponent = ref(null);
const pageTitle = ref('管理后台');
const isCollapse = ref(false);
const defaultAvatar = ref('https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png');

// 计算属性：只显示可见的菜单
const visibleUserMenus = computed(() => {
  return userMenus.value.filter(menu => {
    // 如果菜单本身可见，直接返回
    if (menu.visible !== 0) return true;
    
    // 如果菜单本身不可见，但是有子菜单且至少有一个子菜单可见，则显示父菜单（但不会展开）
    if (menu.children && menu.children.length > 0) {
      return menu.children.some((child: any) => child.visible !== 0);
    }
    
    return false;
  });
});

// 验证图标名称是否有效
const isValidIconName = (iconName?: string) => {
  if (!iconName) return false;
  
  // 检查是否只包含字母数字和连字符/下划线
  const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
  
  // 检查名称是否符合Element Plus图标组件命名规范
  // 图标名称应该是合理的组件名，不能是纯数字或其他无效字符
  return validNamePattern.test(iconName) && isNaN(Number(iconName));
};

// 菜单路径到组件的映射
const menuComponentMap: Record<string, any> = {
  '/dashboard': markRaw(() => import('@/admin/views/dashboard/DashboardView.vue')),
  '/system/user': markRaw(() => import('@/admin/views/system/user/UserManagementView.vue')),
  '/system/role': markRaw(() => import('@/admin/views/system/role/RoleManagementView.vue')),
  '/system/permission': markRaw(() => import('@/admin/views/system/permission/PermissionManagementView.vue')),
  '/system/menu': markRaw(() => import('@/admin/views/system/menu/MenuManagementView.vue')),
  '/content/post': markRaw(() => import('@/admin/views/content/post/PostManagementView.vue')),
  '/content/comment': markRaw(() => import('@/admin/views/content/comment/CommentManagementView.vue')),
  '/audit/post': markRaw(() => import('@/admin/views/audit/post/PostAuditView.vue')),
  '/audit/comment': markRaw(() => import('@/admin/views/audit/comment/CommentAuditView.vue')),
  '/report/comment': markRaw(() => import('@/admin/views/report/comment/CommentReportView.vue')),
  '/report/post': markRaw(() => import('@/admin/views/report/post/PostReportView.vue')),
  '/report/user': markRaw(() => import('@/admin/views/report/user/UserReportView.vue')),
  '/system/announcement': markRaw(() => import('@/admin/views/system/announcement/AnnouncementManagementView.vue')),
  '/system/monitor': markRaw(() => import('@/admin/views/system/monitor/SystemMonitorView.vue')),
  '/system/login-log': markRaw(() => import('@/admin/views/system/login-log/LoginLogManagementView.vue')),
};

// 计算属性
const sidebarWidth = computed(() => isCollapse.value ? '64px' : '220px');

// 加载用户菜单和信息
onMounted(() => {
  loadUserData();
});

const loadUserData = () => {
  try {
    const menusStr = localStorage.getItem('adminMenus');
    const userInfoStr = localStorage.getItem('adminUserInfo');
    
    if (menusStr) {
      userMenus.value = JSON.parse(menusStr);
    } else {
      ElMessage.error('未能获取用户菜单权限');
    }
    
    if (userInfoStr) {
      userInfo.value = JSON.parse(userInfoStr);
      // 即使本地有用户信息，也异步更新为最新信息
      setTimeout(() => {
        fetchCurrentUser();
      }, 0);
    } else {
      // 如果本地没有用户信息，尝试从后端获取
      fetchCurrentUser();
    }
  } catch (error) {
    console.error('加载用户数据失败:', error);
    ElMessage.error('加载用户数据失败');
  }
};

// 获取当前登录用户信息
const fetchCurrentUser = async () => {
  try {
    // 这里需要调用获取当前用户信息的API
    // 假设后端提供了获取当前管理员信息的接口
    const response = await getCurrentUserInfo();
    if (response.code === 1) {
      userInfo.value = response.data;
      // 同时保存到本地存储
      localStorage.setItem('adminUserInfo', JSON.stringify(response.data));
      console.log('成功获取当前用户信息:', response.data);
    } else {
      console.error('获取用户信息失败:', response.msg);
      ElMessage.error(response.msg || '获取用户信息失败');
    }
  } catch (error) {
    console.error('获取当前用户信息失败:', error);
    ElMessage.error('获取当前用户信息失败，请检查网络连接');
  }
};

// 生成菜单路径
const generateMenuPath = (menu: any) => {
  // 如果路径是以/开头，直接返回
  if (menu.path.startsWith('/')) {
    return menu.path;
  }
  // 否则加上/前缀
  return '/' + menu.path;
};

// 处理菜单选择
const handleMenuSelect = (index: string) => {
  // 查找对应的菜单项
  const findMenuById = (menuItems: any[], id: string) => {
    for (const item of menuItems) {
      if (String(item.id) === id) {
        return item;
      }
      
      if (item.children && item.children.length > 0) {
        const found = findMenuById(item.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const selectedMenu = findMenuById(visibleUserMenus.value, index);
  
  // 如果是目录类型的菜单（有子菜单），则不进行页面导航，只更新激活状态
  if (selectedMenu && selectedMenu.children && selectedMenu.children.length > 0) {
    activeMenu.value = index;
    // 目录菜单不应该导航到页面，而是展开子菜单
    return;
  }
  
  // 如果是叶子节点菜单，则导航到对应页面
  if (selectedMenu) {
    activeMenu.value = index;
    navigateToPage(selectedMenu);
  }
};

// 导航到指定页面
const navigateToPage = async (menu: any) => {
  // 更新标题
  pageTitle.value = menu.name;
  
  // 构建路径
  let path = menu.path;
  if (!path.startsWith('/')) {
    path = '/' + path;
  }
  
  // 检查路径是否为目录类型（没有直接对应的组件），如果是，则寻找第一个可用的子菜单
  if (!menuComponentMap[path]) {
    // 从原始菜单数据中查找子菜单，因为visibleUserMenus是树形结构
    const findSubmenu = (menuItems, parentId) => {
      for (const item of menuItems) {
        if (item.parentId === parentId && item.visible !== 0) {
          return item;
        }
        
        // 如果当前项有子项，递归查找
        if (item.children && item.children.length > 0) {
          const subItem = findSubmenu(item.children, parentId);
          if (subItem) {
            return subItem;
          }
        }
      }
      return null;
    };
    
    // 获取完整的菜单数据（这里假设有一个包含所有菜单的变量）
    // 我们可以使用visibleUserMenus作为源数据
    const submenu = findSubmenu(visibleUserMenus.value, menu.id);
    
    if (submenu) {
      // 如果有子菜单，跳转到子菜单
      console.log(`目录菜单 ${menu.name}，跳转到子菜单: ${submenu.name}`);
      navigateToPage(submenu);
      return;
    }
    
    // 如果路径不在映射中，显示提示
    console.warn(`未找到路径 "${path}" 对应的组件`);
    ElMessage.warning(`${menu.name} 页面正在开发中`);
    currentComponent.value = null;
    return;
  }
  
  // 根据路径尝试动态加载组件
  try {
    if (menuComponentMap[path]) {
      const module = await menuComponentMap[path]();
      currentComponent.value = markRaw(module.default);
    } else {
      // 如果路径不在映射中，显示提示
      console.warn(`未找到路径 "${path}" 对应的组件`);
      ElMessage.warning(`${menu.name} 页面正在开发中`);
      currentComponent.value = null;
    }
  } catch (err) {
    console.error(`加载组件失败: ${path}`, err);
    ElMessage.error(`${menu.name} 页面加载失败`);
    currentComponent.value = null;
  }
};

const logout = async () => {
  try {
    await ElMessageBox.confirm(
      '确定要退出管理后台吗？',
      '确认退出',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    );

    // 调用退出登录API
    const response = await adminLogout();
    if(response.code !== 1) {
      console.warn('服务器退出登录失败:', response.msg);
    }

    // 清除管理员token
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUserInfo');
    localStorage.removeItem('adminMenus');
    ElMessage.success('已退出登录');
    router.push('/admin/login');
  } catch (error) {
    console.log('取消退出');
  }
};

const toggleCollapse = () => {
  isCollapse.value = !isCollapse.value;
};

const goToProfile = () => {
  // 跳转到个人资料页面
  pageTitle.value = '个人资料';
  currentComponent.value = null; // 这里可以改为加载实际的个人资料组件
};

const goToDashboard = () => {
  router.push('/admin/dashboard');
  pageTitle.value = '管理后台';
  currentComponent.value = null;
};

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_menu_item = _resolveComponent("el-menu-item")!
  const _component_el_sub_menu = _resolveComponent("el-sub-menu")!
  const _component_el_menu = _resolveComponent("el-menu")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_aside = _resolveComponent("el-aside")!
  const _component_el_avatar = _resolveComponent("el-avatar")!
  const _component_el_dropdown_item = _resolveComponent("el-dropdown-item")!
  const _component_el_dropdown_menu = _resolveComponent("el-dropdown-menu")!
  const _component_el_dropdown = _resolveComponent("el-dropdown")!
  const _component_el_header = _resolveComponent("el-header")!
  const _component_el_main = _resolveComponent("el-main")!
  const _component_el_container = _resolveComponent("el-container")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_el_container, null, {
      default: _withCtx(() => [
        _createVNode(_component_el_aside, {
          width: sidebarWidth.value,
          class: _normalizeClass(["sidebar", { collapsed: isCollapse.value }])
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", {
              class: "logo",
              onClick: goToDashboard
            }, [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(_component_el_icon, { class: "logo-icon" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Management))
                  ]),
                  _: 1
                }),
                _createVNode(_Transition, { name: "fade" }, {
                  default: _withCtx(() => [
                    _withDirectives(_createElementVNode("h3", _hoisted_3, "管理后台", 512), [
                      [_vShow, !isCollapse.value]
                    ])
                  ]),
                  _: 1
                })
              ])
            ]),
            _createVNode(_component_el_menu, {
              "default-active": activeMenu.value,
              class: "sidebar-menu",
              collapse: isCollapse.value,
              "collapse-transition": false,
              onSelect: handleMenuSelect
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleUserMenus.value, (menu) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: menu.id
                  }, [
                    (menu.children && menu.children.length > 0)
                      ? (_openBlock(), _createBlock(_component_el_sub_menu, {
                          key: 0,
                          index: String(menu.id)
                        }, {
                          title: _withCtx(() => [
                            (isValidIconName(menu.icon))
                              ? (_openBlock(), _createBlock(_component_el_icon, { key: 0 }, {
                                  default: _withCtx(() => [
                                    (_openBlock(), _createBlock(_resolveDynamicComponent(menu.icon.toLowerCase())))
                                  ]),
                                  _: 2
                                }, 1024))
                              : _createCommentVNode("", true),
                            _withDirectives(_createElementVNode("span", null, _toDisplayString(menu.name), 513), [
                              [_vShow, !isCollapse.value]
                            ])
                          ]),
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(menu.children, (child) => {
                              return (_openBlock(), _createElementBlock(_Fragment, {
                                key: child.id
                              }, [
                                (child.visible !== 0)
                                  ? (_openBlock(), _createBlock(_component_el_menu_item, {
                                      key: 0,
                                      index: generateMenuPath(child),
                                      onClick: ($event: any) => (navigateToPage(child))
                                    }, {
                                      default: _withCtx(() => [
                                        (isValidIconName(child.icon))
                                          ? (_openBlock(), _createBlock(_component_el_icon, { key: 0 }, {
                                              default: _withCtx(() => [
                                                (_openBlock(), _createBlock(_resolveDynamicComponent(child.icon.toLowerCase())))
                                              ]),
                                              _: 2
                                            }, 1024))
                                          : _createCommentVNode("", true),
                                        _createElementVNode("span", null, _toDisplayString(child.name), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["index", "onClick"]))
                                  : _createCommentVNode("", true)
                              ], 64))
                            }), 128))
                          ]),
                          _: 2
                        }, 1032, ["index"]))
                      : (menu.visible !== 0)
                        ? (_openBlock(), _createBlock(_component_el_menu_item, {
                            key: 1,
                            index: String(menu.id),
                            onClick: ($event: any) => (navigateToPage(menu))
                          }, {
                            default: _withCtx(() => [
                              (isValidIconName(menu.icon))
                                ? (_openBlock(), _createBlock(_component_el_icon, { key: 0 }, {
                                    default: _withCtx(() => [
                                      (_openBlock(), _createBlock(_resolveDynamicComponent(menu.icon.toLowerCase())))
                                    ]),
                                    _: 2
                                  }, 1024))
                                : _createCommentVNode("", true),
                              _createElementVNode("span", null, _toDisplayString(menu.name), 1)
                            ]),
                            _: 2
                          }, 1032, ["index", "onClick"]))
                        : _createCommentVNode("", true)
                  ], 64))
                }), 128))
              ]),
              _: 1
            }, 8, ["default-active", "collapse"]),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_el_button, {
                onClick: toggleCollapse,
                icon: isCollapse.value ? 'Expand' : 'Fold',
                size: "default",
                circle: ""
              }, null, 8, ["icon"])
            ])
          ]),
          _: 1
        }, 8, ["width", "class"]),
        _createVNode(_component_el_container, null, {
          default: _withCtx(() => [
            _createVNode(_component_el_header, { class: "dashboard-header" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("h2", _hoisted_6, _toDisplayString(pageTitle.value), 1),
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_component_el_dropdown, { placement: "bottom-end" }, {
                      dropdown: _withCtx(() => [
                        _createVNode(_component_el_dropdown_menu, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_dropdown_item, { onClick: goToProfile }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_icon, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(User))
                                  ]),
                                  _: 1
                                }),
                                _cache[1] || (_cache[1] = _createTextVNode(" 个人资料 ", -1))
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_el_dropdown_item, {
                              onClick: logout,
                              divided: ""
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_icon, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(SwitchButton))
                                  ]),
                                  _: 1
                                }),
                                _cache[2] || (_cache[2] = _createTextVNode(" 退出登录 ", -1))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_8, [
                          _createVNode(_component_el_avatar, {
                            size: 40,
                            src: userInfo.value?.avatar || defaultAvatar.value
                          }, null, 8, ["src"]),
                          _withDirectives(_createElementVNode("div", _hoisted_9, [
                            _createElementVNode("span", _hoisted_10, _toDisplayString(userInfo.value?.username), 1),
                            _cache[0] || (_cache[0] = _createElementVNode("br", null, null, -1)),
                            _createElementVNode("span", _hoisted_11, _toDisplayString(userInfo.value?.roleName), 1)
                          ], 512), [
                            [_vShow, !isCollapse.value]
                          ])
                        ])
                      ]),
                      _: 1
                    })
                  ])
                ])
              ]),
              _: 1
            }),
            _createVNode(_component_el_main, { class: "main-content" }, {
              default: _withCtx(() => [
                (currentComponent.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(currentComponent.value)))
                    ]))
                  : (_openBlock(), _createBlock(WelcomeSection, {
                      key: 1,
                      userInfo: userInfo.value
                    }, null, 8, ["userInfo"]))
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})